package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.*
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.manager.LumaticaApiManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.w3c.fetch.RequestInit

@Serializable
private data class Konkurrent(
    val id: String = "", val firma: String = "", val branche: String = "",
    val strasse: String = "", val hausnummer: String = "", val plz: String = "", val ort: String = "",
    val webseite: String = "", val email: String = "", val ansprechpartner: String = "",
    val position: String = "", val telefon: String = "", val mobil: String = "", val leadQuelle: String = "Web"
)

@Serializable
private data class KonkurrenzBranche(val id: String = "", val label: String = "")

@Serializable
private data class KonkurrenzInfo(
    val id: String = "", val konkurrentId: String = "", val label: String = "",
    val erstelltVon: String = "", val erstelltAm: String = "", val erstelltUm: String = ""
)

private object KonkurrenzApi {
    private val json = Json { ignoreUnknownKeys = true }
    private const val base = "vertriebsindex/konkurrenz-"

    suspend fun firmen(): List<Konkurrent> = json.decodeFromString(ListSerializer(Konkurrent.serializer()), LumaticaApiManager.call(base + "list"))
    suspend fun branchen(): List<KonkurrenzBranche> = json.decodeFromString(ListSerializer(KonkurrenzBranche.serializer()), LumaticaApiManager.call(base + "branchen"))
    suspend fun infos(): List<KonkurrenzInfo> = json.decodeFromString(ListSerializer(KonkurrenzInfo.serializer()), LumaticaApiManager.call(base + "infos"))

    private suspend fun post(action: String, body: JsonObject): String {
        val response = window.fetch(
            "${LumaticaApiSettings.getBaseUrl()}/api/${base + action}",
            RequestInit(method = "POST", headers = LumaticaApiManager.auditHeaders("application/json"), body = body.toString())
        ).await()
        val result = response.text().await()
        if (!response.ok) error("Konkurrenzindex HTTP ${response.status}: $result")
        return result
    }

    suspend fun saveFirma(item: Konkurrent): Konkurrent = json.decodeFromString(Konkurrent.serializer(), post("save", buildJsonObject {
        put("id", item.id); put("firma", item.firma); put("branche", item.branche)
        put("strasse", item.strasse); put("hausnummer", item.hausnummer); put("plz", item.plz); put("ort", item.ort)
        put("webseite", item.webseite); put("email", item.email); put("ansprechpartner", item.ansprechpartner)
        put("position", item.position); put("telefon", item.telefon); put("mobil", item.mobil)
    }))

    suspend fun deleteFirmen(ids: List<String>) { post("delete", buildJsonObject { put("ids", buildJsonArray { ids.forEach { add(it) } }) }) }
    suspend fun saveBranche(item: KonkurrenzBranche): KonkurrenzBranche = json.decodeFromString(KonkurrenzBranche.serializer(), post("branchen-save", buildJsonObject {
        put("id", item.id); put("label", item.label)
    }))
    suspend fun deleteBranchen(ids: List<String>) { post("branchen-delete", buildJsonObject { put("ids", buildJsonArray { ids.forEach { add(it) } }) }) }
    suspend fun setBranche(ids: List<String>, branche: String): List<Konkurrent> =
        json.decodeFromString(ListSerializer(Konkurrent.serializer()), post("branche-set", buildJsonObject {
            put("ids", buildJsonArray { ids.forEach { add(it) } }); put("branche", branche)
        }))
    suspend fun addInfo(konkurrentId: String, label: String): KonkurrenzInfo = json.decodeFromString(KonkurrenzInfo.serializer(), post("info-save", buildJsonObject {
        put("konkurrentId", konkurrentId); put("label", label)
    }))
    suspend fun deleteInfos(ids: List<String>, konkurrentId: String) { post("info-delete", buildJsonObject {
        put("ids", buildJsonArray { ids.forEach { add(it) } }); put("konkurrentId", konkurrentId)
    }) }
}

private val konkurrenzHeaders = listOf("#", "Firma", "Branche", "Straße", "Hausnummer", "PLZ", "Ort", "Webseite", "Google Maps", "E-Mail", "Ansprechpartner", "Position", "Telefon", "Mobil", "Leadquelle")

private fun Konkurrent.mapsUrl(): String {
    if (listOf(strasse, hausnummer, plz, ort).any { it.isBlank() }) return ""
    val address = "$strasse $hausnummer, $plz $ort"
    return "https://www.google.com/maps/search/?api=1&query=" + (js("encodeURIComponent")(address) as String)
}

private fun Konkurrent.row() = listOf(id, firma, branche, strasse, hausnummer, plz, ort, webseite, mapsUrl(), email, ansprechpartner, position, telefon, mobil, leadQuelle)

private fun KonkurrenzInfo.asLeadInfo() = All(
    id = id, leadInfoText = label, erstelltVon = erstelltVon, erstelltAm = erstelltAm, erstelltUm = erstelltUm
)

@Composable
fun Konkurrenzindex() {
    val scope = rememberCoroutineScope()
    var firmen by remember { mutableStateOf<List<Konkurrent>>(emptyList()) }
    var branchen by remember { mutableStateOf<List<KonkurrenzBranche>>(emptyList()) }
    var infos by remember { mutableStateOf<List<KonkurrenzInfo>>(emptyList()) }
    var selectedIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var infoKonkurrentId by remember { mutableStateOf<String?>(null) }
    var branchenDialog by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf("") }
    var version by remember { mutableStateOf(0) }
    var busy by remember { mutableStateOf(false) }
    val canEdit = RollenService.hasActionPermission("Konkurrenzindex", RollenService.ACTION_EDIT)

    suspend fun reload() {
        firmen = KonkurrenzApi.firmen()
        branchen = KonkurrenzApi.branchen()
        infos = KonkurrenzApi.infos()
        if (infoKonkurrentId != null && firmen.none { it.id == infoKonkurrentId }) infoKonkurrentId = null
        selectedIds = emptySet()
        error = ""
        version++
    }

    LaunchedEffect(Unit) {
        try { reload() }
        catch (e: CancellationException) { throw e }
        catch (e: Exception) { error = e.message ?: "Konkurrenzindex konnte nicht geladen werden." }
    }

    if (branchenDialog) KonkurrenzBranchenDialog(
        onClose = { branchenDialog = false },
        onChanged = {
            branchen = it
            scope.launch {
                try { firmen = KonkurrenzApi.firmen(); selectedIds = emptySet(); version++ }
                catch (e: CancellationException) { throw e }
                catch (e: Exception) { error = e.message ?: "Branchen konnten nicht geladen werden." }
            }
        }
    )

    val infoFirma = firmen.firstOrNull { it.id == infoKonkurrentId }
    if (infoFirma != null) {
        val lead = All(
            id = infoFirma.id, firma = infoFirma.firma,
            leadInfos = infos.filter { it.konkurrentId == infoFirma.id }.map { it.asLeadInfo() }
        )
        PopupEngineCard(
            windowKey = "konkurrenz-info-${infoFirma.id}", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
            widthPx = 900, heightPx = 600, minWidthPx = 520, minHeightPx = 380,
            centered = true, onClose = { infoKonkurrentId = null }
        ) {
            LeadInfoTable(
                lead = lead, enabled = canEdit, onUpdated = {},
                rollenKey = "Konkurrenzindex", screenKey = "Konkurrenzindex_Informationen_${infoFirma.id}",
                addInfo = { current, text ->
                    val saved = KonkurrenzApi.addInfo(current.id, text)
                    infos = infos + saved
                    current.copy(leadInfos = current.leadInfos + saved.asLeadInfo())
                },
                deleteInfo = { current, infoId ->
                    KonkurrenzApi.deleteInfos(listOf(infoId), current.id)
                    infos = infos.filterNot { it.id == infoId && it.konkurrentId == current.id }
                    current.copy(leadInfos = current.leadInfos.filterNot { it.id == infoId })
                }
            )
        }
    }

    if (error.isNotBlank()) Div({ attr("role", "alert") }) { Text(error) }
    key(version) {
        TabularScreen(
            screenKey = "Konkurrenzindex", rollenKey = "Konkurrenzindex", title = "Konkurrenzindex",
            headers = konkurrenzHeaders, fixedLeadingColumns = listOf("#", "Firma"),
            models = firmen.map { All(id = it.id, firma = it.firma) },
            data = firmen.map { it.id to it.row() },
            includeMetadata = false, commitChangedRowsOnly = true,
            editable = canEdit, allowCreate = canEdit, allowSave = canEdit, allowDelete = canEdit,
            allowImportButton = false, allowExportButton = true, allowSettingsButton = false,
            nonEditableCols = setOf("#", "Branche", "Google Maps", "Leadquelle"),
            onSelectedRowsChange = { selectedIds = it },
            extraNavButtons = listOf(
                TabularToolbarButton(
                    label = "Information", icon = "images/info.svg", enabled = selectedIds.size == 1,
                    onClick = { infoKonkurrentId = selectedIds.singleOrNull() }
                ),
                TabularToolbarButton(
                    label = "Branche", icon = "images/filter.svg", enabled = canEdit && !busy,
                    dropdownOptions = listOf("__verwalten__" to "Branchen anlegen", "" to "Leer") + branchen.map { it.label to it.label },
                    selectedDropdownValue = selectedIds.mapNotNull { id -> firmen.firstOrNull { it.id == id }?.branche }.distinct().singleOrNull(),
                    dropdownPlaceholder = "Branche zuweisen",
                    onDropdownChange = { value ->
                        if (value == "__verwalten__") branchenDialog = true
                        else if (value != null) {
                            if (selectedIds.isEmpty()) error = "Bitte zuerst mindestens einen Konkurrenten auswählen."
                            else {
                                val ids = selectedIds.toList()
                                busy = true
                                scope.launch {
                                    try {
                                        val updated = KonkurrenzApi.setBranche(ids, value).associateBy { it.id }
                                        firmen = firmen.map { updated[it.id] ?: it }
                                        selectedIds = emptySet()
                                        error = ""
                                        version++
                                    } catch (e: CancellationException) { throw e }
                                    catch (e: Exception) { error = e.message ?: "Branche konnte nicht zugewiesen werden." }
                                    finally { busy = false }
                                }
                            }
                        }
                    }
                )
            ),
            onRefresh = { scope.launch { try { reload() } catch (e: CancellationException) { throw e } catch (e: Exception) { error = e.message.orEmpty() } } },
            onCommit = {},
            onCommitConfirmed = { rows, complete -> scope.launch {
                try {
                    rows.forEach { (id, cols) ->
                        val old = firmen.firstOrNull { it.id == id }
                        KonkurrenzApi.saveFirma(Konkurrent(
                            id = old?.id.orEmpty(), firma = cols.getOrNull(1).orEmpty().trim(), branche = old?.branche.orEmpty(),
                            strasse = cols.getOrNull(3).orEmpty().trim(), hausnummer = cols.getOrNull(4).orEmpty().trim(),
                            plz = cols.getOrNull(5).orEmpty().trim(), ort = cols.getOrNull(6).orEmpty().trim(),
                            webseite = cols.getOrNull(7).orEmpty().trim(), email = cols.getOrNull(9).orEmpty().trim(),
                            ansprechpartner = cols.getOrNull(10).orEmpty().trim(), position = cols.getOrNull(11).orEmpty().trim(),
                            telefon = cols.getOrNull(12).orEmpty().trim(), mobil = cols.getOrNull(13).orEmpty().trim()
                        ))
                    }
                    complete(true)
                    reload()
                } catch (e: CancellationException) { complete(false); throw e }
                catch (e: Exception) { error = e.message ?: "Konkurrent konnte nicht gespeichert werden."; complete(false) }
            } },
            onDeleteRows = { ids ->
                KonkurrenzApi.deleteFirmen(ids)
                infoKonkurrentId = null
                reload()
            }
        )
    }
}

@Composable
private fun KonkurrenzBranchenDialog(onClose: () -> Unit, onChanged: (List<KonkurrenzBranche>) -> Unit) {
    val scope = rememberCoroutineScope()
    var branchen by remember { mutableStateOf<List<KonkurrenzBranche>>(emptyList()) }
    var error by remember { mutableStateOf("") }
    var version by remember { mutableStateOf(0) }
    suspend fun reload() { branchen = KonkurrenzApi.branchen(); onChanged(branchen); version++ }
    LaunchedEffect(Unit) { try { reload() } catch (e: CancellationException) { throw e } catch (e: Exception) { error = e.message.orEmpty() } }
    PopupEngineCard(
        windowKey = "konkurrenz-branchen", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 880, heightPx = 640, minWidthPx = 560, minHeightPx = 420,
        centered = true, viewportAnchored = true, resizable = true, onClose = onClose
    ) {
        Div({ style { width(100.percent); height(100.percent); padding(18.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
            if (error.isNotBlank()) Div({ attr("role", "alert") }) { Text(error) }
            key(version) {
                TabularScreen(
                    screenKey = "Konkurrenzbranchen", rollenKey = "Konkurrenzindex", title = "Branchen verwalten",
                    headers = listOf("#", "Branche"), fixedLeadingColumns = listOf("#"),
                    models = branchen.map { All(id = it.id, label = it.label) },
                    data = branchen.map { it.id to listOf(it.id, it.label) },
                    editable = true, commitChangedRowsOnly = true, includeMetadata = false,
                    allowImportButton = false, allowExportButton = false, allowSettingsButton = false,
                    createButtonLabel = "Branche anlegen", nonEditableCols = setOf("#"),
                    onRefresh = { scope.launch { try { reload() } catch (e: CancellationException) { throw e } catch (e: Exception) { error = e.message.orEmpty() } } },
                    onCommit = {},
                    onCommitConfirmed = { rows, complete -> scope.launch {
                        try {
                            rows.forEach { (id, cols) ->
                                KonkurrenzApi.saveBranche(KonkurrenzBranche(id.takeIf { rowId -> branchen.any { it.id == rowId } }.orEmpty(), cols.getOrNull(1).orEmpty().trim()))
                            }
                            complete(true)
                            reload()
                            error = ""
                        } catch (e: CancellationException) { complete(false); throw e }
                        catch (e: Exception) { error = e.message ?: "Branche konnte nicht gespeichert werden."; complete(false) }
                    } },
                    onDeleteRows = { ids -> KonkurrenzApi.deleteBranchen(ids); reload() }
                )
            }
        }
    }
}
